/**
 * Customer Vendor Subscription API client — supplier-truth commitments
 * (cr001_customer_vendor_subscriptionses).
 *
 * Read-only here; used by the reconciliation customer drill-down to show the
 * subscriptions a customer holds. Mirrors the pure-fetch service pattern.
 */

import type {
  CustomerVendorSubscription,
  CustomerVendorSubscriptionListResponse,
  CreateCustomerVendorSubscriptionRequest,
  UpdateCustomerVendorSubscriptionRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

/** Create a subscription (the commitment) — e.g. when resolving a
 *  no-subscription variance by creating the missing record. */
export async function createVendorSubscription(
  payload: CreateCustomerVendorSubscriptionRequest,
  signal?: AbortSignal,
): Promise<CustomerVendorSubscription> {
  const response = await fetch(`${API_BASE_URL}/vendor-subscriptions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const data = await response
      .json()
      .catch(() => ({ error: `Failed to create subscription: ${response.status}` }));
    throw new Error(data.error ?? `Failed to create subscription: ${response.status}`);
  }
  return (await response.json()) as CustomerVendorSubscription;
}

export async function getVendorSubscriptions(
  params: { customerId?: string; vendorId?: string; vendorProductId?: string } = {},
  signal?: AbortSignal,
): Promise<CustomerVendorSubscription[]> {
  const qs = new URLSearchParams();
  if (params.customerId) qs.set('customerId', params.customerId);
  if (params.vendorId) qs.set('vendorId', params.vendorId);
  if (params.vendorProductId) qs.set('vendorProductId', params.vendorProductId);
  const query = qs.toString();
  const response = await fetch(`${API_BASE_URL}/vendor-subscriptions${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    const data = await response
      .json()
      .catch(() => ({ error: `Failed to fetch subscriptions: ${response.status}` }));
    throw new Error(data.error ?? `Failed to fetch subscriptions: ${response.status}`);
  }
  const data: CustomerVendorSubscriptionListResponse = await response.json();
  return data.items ?? [];
}

/** Update a subscription (the commitment) — e.g. bump seats when a customer changes count. */
export async function updateVendorSubscription(
  id: string,
  payload: UpdateCustomerVendorSubscriptionRequest,
  signal?: AbortSignal,
): Promise<CustomerVendorSubscription> {
  const response = await fetch(`${API_BASE_URL}/vendor-subscriptions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const data = await response
      .json()
      .catch(() => ({ error: `Failed to update subscription: ${response.status}` }));
    throw new Error(data.error ?? `Failed to update subscription: ${response.status}`);
  }
  return (await response.json()) as CustomerVendorSubscription;
}
